import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, Save, Trash2, X } from "lucide-react";
import { ROLES, ROLE_ICON, roleLabel } from "@/components/schedule/roles";

/**
 * CellEditPopover — inline editor for a schedule entry cell.
 *
 * - Toggle OFF/working.
 * - time_in / time_out inputs (24h HH:MM format).
 * - notes textarea.
 * - Save → calls onSave(patch). Delete → onDelete(). Close → onClose().
 */
export default function CellEditPopover({
  open,
  onOpenChange,
  entry,
  onSave,
  onDelete,
  trigger,
}) {
  const { t } = useTranslation();
  const [tIn, setTIn] = useState(entry?.time_in || "");
  const [tOut, setTOut] = useState(entry?.time_out || "");
  const [isOff, setIsOff] = useState(!!entry?.is_off);
  const [notes, setNotes] = useState(entry?.notes || "");
  const [role, setRole] = useState(entry?.role || "regular");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTIn(entry?.time_in || "");
    setTOut(entry?.time_out || "");
    setIsOff(!!entry?.is_off);
    setNotes(entry?.notes || "");
    setRole(entry?.role || "regular");
  }, [entry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {
        time_in: isOff ? null : tIn || null,
        time_out: isOff ? null : tOut || null,
        is_off: isOff,
        notes: notes || null,
      };
      // Only send role if it actually changed — backend propagates it
      // across all the employee's entries.
      if (role !== (entry?.role || "regular")) {
        patch.role = role;
      }
      await onSave(patch);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-72"
        data-testid="schedule-cell-edit"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">
              {entry?.employee_name}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {entry?.day_date}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md bg-muted/40 p-2">
            <Label className="text-xs">{t("schedule.cell.isOff")}</Label>
            <Switch
              checked={isOff}
              onCheckedChange={setIsOff}
              data-testid="schedule-cell-off-toggle"
            />
          </div>

          {!isOff && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t("schedule.cell.timeIn")}
                </Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="time"
                    value={tIn}
                    onChange={(e) => setTIn(e.target.value)}
                    className="h-8 pl-7 text-xs"
                    data-testid="schedule-cell-time-in"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t("schedule.cell.timeOut")}
                </Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="time"
                    value={tOut}
                    onChange={(e) => setTOut(e.target.value)}
                    className="h-8 pl-7 text-xs"
                    data-testid="schedule-cell-time-out"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("schedule.cell.notes")}
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("schedule.cell.notesPlaceholder")}
              className="min-h-[60px] text-xs"
              data-testid="schedule-cell-notes"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("schedule.cell.role")}
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger
                className="h-8 text-xs"
                data-testid="schedule-cell-role"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_ICON[r]} {roleLabel(t, r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[9px] text-muted-foreground">
              {t("schedule.cell.roleHint")}
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-7 flex-1 text-xs"
              data-testid="schedule-cell-save"
            >
              <Save className="mr-1 h-3 w-3" />
              {t("common.save")}
            </Button>
            {onDelete && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  await onDelete();
                  onOpenChange(false);
                }}
                className="h-7 text-xs text-destructive hover:bg-destructive/10"
                data-testid="schedule-cell-delete"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-7 text-xs"
              data-testid="schedule-cell-cancel"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
